import React, { useEffect, useState } from 'react';
import {
  Version,
  getVersions,
  createVersion,
  updateVersion,
  deleteVersion,
} from '../api/admin';
import styles from './CommonPage.module.css'; // Reuse CommonPage styles for consistency
import { Edit, Plus, Trash2 } from 'lucide-react';

const VersionPage: React.FC = () => {
  const [versions, setVersions] = useState<Version[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingVersion, setEditingVersion] = useState<Version | null>(null);
  const [formData, setFormData] = useState<Omit<Version, 'id'>>({
    platform: '',
    type: 'manager',
    minVersion: '',
    latestVersion: '',
    fileUrl: '',
    enabled: true,
  });

  useEffect(() => {
    fetchVersions();
  }, []);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [isModalOpen]);

  const fetchVersions = async () => {
    try {
      const data = await getVersions();
      setVersions(data);
    } catch (error) {
      console.error('Failed to fetch versions', error);
      alert('버전 목록을 불러오는데 실패했습니다.');
    }
  };

  const handleOpenModal = (version: Version | null = null) => {
    if (version) {
      setEditingVersion(version);
      setFormData({
        platform: version.platform,
        type: version.type || 'manager',
        minVersion: version.minVersion,
        latestVersion: version.latestVersion,
        fileUrl: version.fileUrl,
        enabled: version.enabled,
      });
    } else {
      setEditingVersion(null);
      setFormData({
        platform: 'windows',
        type: 'manager',
        minVersion: '',
        latestVersion: '',
        fileUrl: '',
        enabled: true,
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingVersion(null);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.platform || !formData.latestVersion) {
      alert('플랫폼과 최신 버전을 입력해주세요.');
      return;
    }

    try {
      if (editingVersion) {
        await updateVersion(editingVersion.id, formData);
        alert('버전이 수정되었습니다.');
      } else {
        await createVersion(formData);
        alert('버전이 추가되었습니다.');
      }
      fetchVersions();
      handleCloseModal();
    } catch (error) {
      console.error('Failed to save version', error);
      alert('버전 저장에 실패했습니다.');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('정말 삭제하시겠습니까?')) {
      try {
        await deleteVersion(id);
        alert('삭제되었습니다.');
        fetchVersions();
      } catch (error) {
        console.error('Failed to delete version', error);
        alert('삭제에 실패했습니다.');
      }
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>버전 관리</h2>
        <button className={styles.primaryBtn} onClick={() => handleOpenModal()}>
          <Plus size={20} />
          버전 추가
        </button>
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>ID</th>
              <th>플랫폼</th>
              <th>타입</th>
              <th>최소 버전</th>
              <th>최신 버전</th>
              <th>URL</th>
              <th>상태</th>
              <th style={{ textAlign: 'center' }}>관리</th>
            </tr>
          </thead>
          <tbody>
            {versions.map((version) => (
              <tr key={version.id}>
                <td data-label="ID">{version.id}</td>
                <td data-label="플랫폼">{version.platform}</td>
                <td data-label="타입">{version.type}</td>
                <td data-label="최소 버전">{version.minVersion}</td>
                <td data-label="최신 버전">{version.latestVersion}</td>
                <td data-label="URL" style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={version.fileUrl}>{version.fileUrl}</td>
                <td data-label="상태">
                  <span className={version.enabled ? styles.active : styles.expired}>
                    {version.enabled ? '활성' : '비활성'}
                  </span>
                </td>
                <td data-label="관리" style={{ textAlign: 'center' }}>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center' }}>
                    <button
                      onClick={() => handleOpenModal(version)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3b82f6', display: 'flex' }}
                      title="수정"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(version.id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', display: 'flex' }}
                      title="삭제"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h3>{editingVersion ? '버전 수정' : '버전 추가'}</h3>
            <form onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label>플랫폼</label>
                <div className={styles.radioGroup}>
                  <label className={`${styles.radioLabel} ${styles.radioActive}`}>
                    <input
                      type="radio"
                      name="platform"
                      value="windows"
                      checked={true}
                      readOnly
                    />
                    Windows
                  </label>
                </div>
              </div>
              <div className={styles.formGroup}>
                <label>타입</label>
                <div className={styles.radioGroup}>
                  <label className={`${styles.radioLabel} ${formData.type === 'manager' ? styles.radioActive : ''}`}>
                    <input
                      type="radio"
                      name="type"
                      value="manager"
                      checked={formData.type === 'manager'}
                      onChange={handleChange}
                    />
                    Manager (상담원)
                  </label>
                  <label className={`${styles.radioLabel} ${formData.type === 'agent' ? styles.radioActive : ''}`}>
                    <input
                      type="radio"
                      name="type"
                      value="agent"
                      checked={formData.type === 'agent'}
                      onChange={handleChange}
                    />
                    Agent (고객)
                  </label>
                </div>
              </div>
              <div className={styles.formGroup}>
                <label>최소 버전</label>
                <input
                  type="text"
                  name="minVersion"
                  value={formData.minVersion}
                  onChange={handleChange}
                  placeholder="예: 1.0.0"
                />
              </div>
              <div className={styles.formGroup}>
                <label>최신 버전</label>
                <input
                  type="text"
                  name="latestVersion"
                  value={formData.latestVersion}
                  onChange={handleChange}
                  placeholder="예: 1.2.0"
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label>다운로드 URL</label>
                <input
                  type="text"
                  name="fileUrl"
                  value={formData.fileUrl}
                  onChange={handleChange}
                  placeholder="https://..."
                />
              </div>
              <div className={styles.formGroup}>
                <label>
                  <input
                    type="checkbox"
                    name="enabled"
                    checked={formData.enabled}
                    onChange={handleChange}
                    style={{ marginRight: '8px', width: 'auto' }}
                  />
                  활성화
                </label>
              </div>
              <div className={styles.formActions}>
                <button type="button" onClick={handleCloseModal} className={styles.cancelBtn}>
                  취소
                </button>
                <button type="submit" className={styles.submitBtn}>
                  저장
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VersionPage;
